import React, {useState} from 'react';
import {Box, Slider, TextField} from '@mui/material';

export const MIN_AMOUNT = 1;
export const MAX_AMOUNT = 100;

interface Props {
    amount: number;
    onAmountChange: (amount: number) => void;
}

const clamp = (value: number) => Math.min(MAX_AMOUNT, Math.max(MIN_AMOUNT, value));

const AmountStep = ({amount, onAmountChange}: Props) => {
    // Mirror the input as text so the user can clear it while typing;
    // clamped back to a valid amount on blur.
    const [inputValue, setInputValue] = useState(String(amount));

    const handleInputChange = (raw: string) => {
        setInputValue(raw);
        const parsed = parseInt(raw, 10);
        if (!isNaN(parsed)) {
            onAmountChange(clamp(parsed));
        }
    };

    const handleInputBlur = () => {
        const parsed = parseInt(inputValue, 10);
        const next = isNaN(parsed) ? MIN_AMOUNT : clamp(parsed);
        onAmountChange(next);
        setInputValue(String(next));
    };

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, py: 2}}>
            {/* The slider thumb overhangs the track by ~10px; the margins keep it
                inside the content edges so everything stays flush with the other
                steps and the footer. */}
            <Slider
                value={amount}
                onChange={(_e, value) => {
                    const next = clamp(value as number);
                    onAmountChange(next);
                    setInputValue(String(next));
                }}
                min={MIN_AMOUNT}
                max={MAX_AMOUNT}
                valueLabelDisplay="auto"
                sx={{flex: 1, mx: '10px'}}
            />
            <TextField
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onBlur={handleInputBlur}
                size="small"
                type="number"
                slotProps={{htmlInput: {min: MIN_AMOUNT, max: MAX_AMOUNT, style: {textAlign: 'center'}}}}
                sx={{width: '90px', flexShrink: 0}}
            />
        </Box>
    );
};

export default AmountStep;
